// src/tattoo/LeftPanel.jsx
import React from "react";
import {
  tattoo,
  clearAll,
  downloadUVGuide,
  downloadAllUVGuides,
  setPoseWeight,
} from "./state";
import LayersPanel from "./LayersPanel.jsx";
import AuthButton from "./components/AuthButton.jsx";
import LeadFormSection from "./leftPanel/sections/LeadFormSection";
import ArtistWizardSection from "./leftPanel/sections/ArtistWizardSection";
import JoinWizardSection from "./leftPanel/sections/JoinWizardSection";
import LeftPanelMobileContent from "./LeftPanelMobileContent.jsx";
import ArtistDirectorySection from "./leftPanel/sections/ArtistDirectorySection";
import FlashSection from "./leftPanel/sections/FlashSection";
import ProjectNameField from "./leftPanel/ProjectNameField";
import { useLeftPanelState } from "./leftPanel/useLeftPanelState";
import SkinToneSection from "./leftPanel/sections/SkinToneSection";
import AppearanceSection from "./leftPanel/sections/AppearanceSection";
import LayersSection from "./leftPanel/sections/LayersSection";

export default function LeftPanel({
  readOnly,
  renderedImageUrls,
  placementUrl,
  onRefreshPreview,
  onExportPreview,
  onExportPreviewVideo,
  panelMode = "layers", // ✅ add
  setPanelMode = () => {}, // ✅ add
  isMobile = false, // ✅ add
}) {
  const lp = useLeftPanelState({ readOnly, isMobile });
  const {
    snap, containerRef, layerFileRef, decalFileRef,
    sex, opts, layersPartKey, uiLayers, hasSomethingToShare,
    viewerReadOnly, hasShopProfile, shopNeedsReactivation, hairIsNone,
    showPaint, setShowPaint, showTemplates, setShowTemplates, showScrollTop,
    quoteOpen, setQuoteOpen, showJoin, setShowJoin,
    showArtistWizard, setShowArtistWizard,
    showArtistUpgrade, setShowArtistUpgrade,
    openArtistWizardGated, artistTrialStarted, artistTrialDaysLeft,
    shopProfile, shopProfileLoading,
    preparedVideo, setPreparedVideo, videoBusy, setVideoBusy,
    videoPreset, setVideoPreset,
    skinOpen, setSkinOpen, appearanceOpen, setAppearanceOpen,
    topMode, topPreset, topCustom,
    bottomMode, bottomPreset, bottomCustom,
    hairMode, hairPreset, hairCustom,
    onUploadLayer, onUploadDecal, setTop, setBottom, setHair,
    onSubmitArtistProfile,
  } = lp;

  if (viewerReadOnly) {
    return (
      <aside
        ref={containerRef}
        className="left-panel tt-panel tt-col tt-pad-m"
        style={{
          gap: 14,
          maxHeight: "96vh",
          overflowY: "auto",
          position: "relative",
          zIndex: 50,
        }}
      >
        {/* keep your normal header (logo + auth/email) */}
        <div
          style={{
            position: "relative",
            top: 0,
            zIndex: 20,
            paddingBottom: 0,
            marginBottom: 0,
          }}
        >
          <div
            className="tt-row"
            style={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div className="tt-row" style={{ gap: 8, alignItems: "center" }}>
              {!isMobile && (
                <img src="/tattoo/images/TatTune.png" width="90" alt="Shop" />
              )}
              <AuthButton className="tt-auth-btn tt-grow" />
            </div>

            {/* mobile collapse button still ok */}
            {isMobile && (
              <button
                type="button"
                className="tt-btn tt-btn--icon"
                onClick={() => (tattoo.leftOpen = !snap.leftOpen)}
                title={snap.leftOpen ? "Hide feedback" : "Show feedback"}
                style={{ width: 36, height: 36, borderRadius: 8 }}
              >
                {snap.leftOpen ? "—" : "≡"}
              </button>
            )}
          </div>
        </div>

        {/* project name (read only) */}
        {!tattoo.readOnly && <ProjectNameField readOnly />}

        {/* layers + comments only (no uploads/templates/clear/share/etc) */}
        <div className="section">
          <div className="label">Feedback</div>
          <LayersPanel partKey={layersPartKey} layers={uiLayers} readOnly />
        </div>
      </aside>
    );
  }

  function poseThumbSrc(label) {
    const t = String(label || "")
      .trim()
      .toLowerCase();

    if (t === "neutral") return "/poses/Neutral.svg";
    if (t === "Right Arm" || t === "Right Arm") return "/poses/A-Pose.svg";

    // fallback
    return "/poses/Neutral.svg";
  }

  // ✅ MOBILE: content-only (the sheet owns scroll + sticky)
  if (isMobile) {
    return (
      <LeftPanelMobileContent
        lp={lp}
        panelMode={panelMode}
        setPanelMode={setPanelMode}
        onJumpToArtistDirectory={() => {
          const el = document.getElementById("artist-directory");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        renderedImageUrls={renderedImageUrls}
        placementUrl={placementUrl}
        onRefreshPreview={onRefreshPreview}
        onExportPreviewVideo={onExportPreviewVideo}
        onExportPreview={onExportPreview}
      />
    );
  }

  // ✅ DESKTOP: keep your existing full <aside> version
  return (
    <aside
      ref={containerRef}
      className="left-panel tt-panel tt-col tt-pad-m"
      style={{
        gap: 14,

        // ✅ DESKTOP keeps old behavior
        maxHeight: isMobile ? "none" : "96vh",
        overflowY: isMobile ? "visible" : "auto",

        // ✅ IMPORTANT: allow the *parent sheet scroller* to scroll
        height: isMobile ? "auto" : undefined,

        position: "relative",
      }}
    >
      {/* Header: logo + account + mobile collapse toggle */}
      <div
        style={{
          position: "relative",
          top: 0,
          zIndex: 20,
          paddingBottom: 0,
          marginBottom: 0,
          background: "transparent",
          borderBottom: "none",
        }}
      >
        <div
          className="tt-row"
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          {/* LEFT: logo + account */}
          <div className="tt-row" style={{ gap: 8, alignItems: "center" }}>
            {!isMobile && (
              <img src="/tattoo/images/TatTune.png" width="90" alt="Shop" />
            )}
            <AuthButton className="tt-auth-btn tt-grow" />
          </div>
          {/* Left: Find an artist */}
          <button
            type="button"
            className="tt-btn tt-find-artist"
            onClick={() => {
              const el = document.getElementById("artist-directory");
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
          >
            Find an artist ↓
          </button>
          {/* RIGHT: collapse / expand – mobile only */}
          {isMobile && (
            <button
              type="button"
              className="tt-btn tt-btn--icon"
              onClick={() => (tattoo.leftOpen = !snap.leftOpen)}
              title={snap.leftOpen ? "Hide design panel" : "Show design panel"}
              style={{ width: 36, height: 36, borderRadius: 8 }}
            >
              {snap.leftOpen ? "—" : "≡"}
            </button>
          )}
        </div>
        <div style={{ marginTop: 10 }}>
          <ProjectNameField />

          <LayersSection
            layerFileRef={layerFileRef} decalFileRef={decalFileRef}
            onUploadLayer={onUploadLayer} onUploadDecal={onUploadDecal}
            onOpenArtistWizard={openArtistWizardGated}
            setShowArtistWizard={setShowArtistWizard}
            shopProfileLoading={shopProfileLoading} hasShopProfile={hasShopProfile}
            shopNeedsReactivation={shopNeedsReactivation}
            readOnly={snap.readOnly}
            onExportPreview={onExportPreview} onExportPreviewVideo={onExportPreviewVideo}
            preparedVideo={preparedVideo} setPreparedVideo={setPreparedVideo}
            videoBusy={videoBusy} setVideoBusy={setVideoBusy}
            videoPreset={videoPreset} setVideoPreset={setVideoPreset}
            layersPartKey={layersPartKey} uiLayers={uiLayers}
            variant="desktop"
          />
        </div>
      </div>
      <AppearanceSection
        snap={snap} opts={opts} hairIsNone={hairIsNone}
        appearanceOpen={appearanceOpen} setAppearanceOpen={setAppearanceOpen}
        hairMode={hairMode} hairPreset={hairPreset} hairCustom={hairCustom}
        topMode={topMode} topPreset={topPreset} topCustom={topCustom}
        bottomMode={bottomMode} bottomPreset={bottomPreset} bottomCustom={bottomCustom}
        setHair={setHair} setTop={setTop} setBottom={setBottom}
      />
      <SkinToneSection snap={snap} skinOpen={skinOpen} setSkinOpen={setSkinOpen} variant="desktop" />

      {/* Pose morphs (separate from body morphs) */}
      <div className="section">
        <div className="label">Pose</div>

        {snap.poseMorphNames?.length ? (
          <div
            className="tt-row"
            style={{
              display: isMobile ? "grid" : "flex",
              gridTemplateColumns: isMobile
                ? "repeat(auto-fill, minmax(56px, 1fr))"
                : undefined,
              gap: 8,
              flexWrap: isMobile ? undefined : "wrap",
            }}
          >
            {snap.poseMorphNames.map((fullName) => {
              const label = String(fullName).replace(/^POSE__/, "");
              const w = (snap.poseWeights && snap.poseWeights[fullName]) || 0;
              const isOn = w > 0.5;

              const src = poseThumbSrc(label);

              return (
                <button
                  key={fullName}
                  type="button"
                  className={`tt-chip ${isOn ? "is-selected" : ""}`}
                  onClick={() => setPoseWeight(fullName, 1)}
                  title={`${label} (${Math.round(w * 100)}%)`}
                  aria-label={label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: isMobile ? 8 : "8px 10px",
                  }}
                >
                  <img
                    src={src}
                    alt=""
                    draggable={false}
                    style={{
                      width: isMobile ? 34 : 20,
                      height: isMobile ? 34 : 20,
                      opacity: isOn ? 1 : 0.85,
                    }}
                  />
                  {!isMobile && <span>{label}</span>}
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.35 }}>
            Pose morphs not detected yet. (They’ll appear after the model loads
            and calls <code>setPoseMorphNames()</code>.)
          </div>
        )}
      </div>

      {/* Templates */}
      <div className="section">
        <div className="label">
          Download Templates{" "}
          <button
            className="mini-toggle"
            onClick={() => setShowTemplates((s) => !s)}
          >
            {showTemplates ? "–" : "+"}
          </button>
        </div>
        {showTemplates && (
          <div className="tt-row" style={{ flexWrap: "wrap", gap: 8 }}>
            {Object.entries(snap.uvGuides || {}).map(([k, url]) =>
              url ? (
                <button
                  key={k}
                  type="button"
                  className="tt-chip"
                  onClick={() => downloadUVGuide(k)}
                >
                  {k.replace("torso", "Torso").replace("legs", "Legs")}
                </button>
              ) : null
            )}
            <button
              type="button"
              className="tt-chip"
              onClick={downloadAllUVGuides}
            >
              Download all
            </button>
          </div>
        )}
      </div>

      <FlashSection />

      {!hasSomethingToShare && <ArtistDirectorySection />}

      {!snap.readOnly && hasSomethingToShare && (
        <LeadFormSection
          renderedImageUrls={renderedImageUrls}
          placementUrl={placementUrl}
          onRefreshPreview={onRefreshPreview}
          quoteOpen={quoteOpen}
          setQuoteOpen={setQuoteOpen}
        />
      )}

      {/* Clear all */}
      <div className="tt-row clear-all-btn" style={{ marginTop: "auto" }}>
        <button
          type="button"
          className="tt-btn tt-btn--fill-danger tt-grow"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (
              typeof window !== "undefined" &&
              window.confirm(
                "Clear all layers and settings for this design?\n\nThis can’t be undone."
              )
            ) {
              clearAll();
            }
          }}
        >
          Clear all Artwork
        </button>
      </div>

      {/* Scroll-to-top chevron */}
      {showScrollTop && (
        <button
          type="button"
          className="tt-scroll-top"
          onClick={() => {
            if (!containerRef.current) return;
            containerRef.current.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }}
        >
          ↑
        </button>
      )}

      <JoinWizardSection showJoin={showJoin} setShowJoin={setShowJoin} />
      <ArtistWizardSection
        showArtistWizard={showArtistWizard} setShowArtistWizard={setShowArtistWizard}
        shopProfile={shopProfile} onSubmitArtistProfile={onSubmitArtistProfile}
        showArtistUpgrade={showArtistUpgrade} setShowArtistUpgrade={setShowArtistUpgrade}
        artistTrialStarted={artistTrialStarted} artistTrialDaysLeft={artistTrialDaysLeft}
      />
    </aside>
  );
}
